/**
 * Dashboard Service - Frontend
 * @description Optimized service for dashboard endpoints
 * Uses the new backend endpoints for better performance
 * Uses mappers to transform backend data (shortened fields) to frontend data (full fields)
 */

import { api } from '../config';
import { ApiResponse } from '@/types';
import {
  DashboardDevice,
  DashboardOperation,
  DashboardLecture,
  DashboardLecturePage,
} from '@/types/dashboard.types';
import {
  BackendApiResponse,
  BackendDevice,
  BackendDashboardOperation,
  BackendDashboardLecture,
  BackendDashboardLecturePage,
} from '@/types/backend.types';
import { DashboardMapper } from '@/mappers/dashboard.mapper';
import { ApiMapper } from '@/mappers/api.mapper';

// Query parameters for dashboard endpoints
export interface DashboardQueryParams {
  limit?: number;
  cursor?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const dashboardService = {
  /**
   * Get optimized devices for dashboard
   * Returns devices with operation counts and performance metrics
   * @param userId - User ID to get devices for
   * @returns Promise with dashboard-optimized device data
   */
  async getDevicesByUserId(userId: string): Promise<ApiResponse<DashboardDevice[]>> {
    const response = await api.get<BackendApiResponse<BackendDevice[]>>(
      `/dashboard/devices/${userId}`
    );
    return ApiMapper.mapResponseArray(response.data, DashboardMapper.deviceToFrontend);
  },

  /**
   * Get optimized operations for a specific device
   * Returns operations with lecture counts
   * @param deviceId - Device ID to get operations for
   * @param params - Optional query parameters
   * @returns Promise with dashboard-optimized operation data
   */
  async getOperationsByDeviceId(
    deviceId: string,
    params?: DashboardQueryParams
  ): Promise<ApiResponse<DashboardOperation[]>> {
    const response = await api.get<BackendApiResponse<BackendDashboardOperation[]>>(
      `/dashboard/operations/${deviceId}`,
      { params }
    );
    return ApiMapper.mapResponseArray(response.data, DashboardMapper.operationToFrontend);
  },

  /**
   * Get optimized lectures for a specific operation
   * Returns lectures with essential data only
   * @param operationId - Operation ID to get lectures for
   * @param params - Optional query parameters
   * @returns Promise with dashboard-optimized lecture data
   */
  async getLecturesByOperationId(
    operationId: string,
    params?: DashboardQueryParams
  ): Promise<ApiResponse<DashboardLecture[]>> {
    const pageResponse = await this.getLecturesByOperationIdPage(operationId, params);
    if (!pageResponse.success) {
      return pageResponse;
    }
    return {
      success: true,
      data: pageResponse.data.items,
      message: pageResponse.message,
    };
  },

  async getLecturesByOperationIdPage(
    operationId: string,
    params?: DashboardQueryParams
  ): Promise<ApiResponse<DashboardLecturePage>> {
    const response = await api.get<BackendApiResponse<BackendDashboardLecturePage>>(
      `/dashboard/lectures/${operationId}`,
      { params }
    );
    if (!response.data.success) {
      return {
        success: false,
        error: response.data.error || { code: 'EXPORT_ERROR', message: 'Request failed' },
        message: response.data.message,
      };
    }
    const pageData = response.data.data;
    return {
      success: true,
      data: {
        items: pageData.items.map(DashboardMapper.lectureToFrontend),
        hasMore: pageData.hasMore,
        nextCursor: pageData.nextCursor,
      },
      message: response.data.message,
    };
  },

  /**
   * Get optimized lectures for a specific device
   * Returns all lectures for a device across all operations
   * @param deviceId - Device ID to get lectures for
   * @param params - Optional query parameters
   * @returns Promise with dashboard-optimized lecture data
   */
  async getLecturesByDeviceId(
    deviceId: string,
    params?: DashboardQueryParams
  ): Promise<ApiResponse<DashboardLecture[]>> {
    const pageResponse = await this.getLecturesByDeviceIdPage(deviceId, params);
    if (!pageResponse.success) {
      return pageResponse;
    }
    return {
      success: true,
      data: pageResponse.data.items,
      message: pageResponse.message,
    };
  },

  async getLecturesByDeviceIdPage(
    deviceId: string,
    params?: DashboardQueryParams
  ): Promise<ApiResponse<DashboardLecturePage>> {
    const response = await api.get<BackendApiResponse<BackendDashboardLecturePage>>(
      `/dashboard/lectures/device/${deviceId}`,
      { params }
    );
    if (!response.data.success) {
      return {
        success: false,
        error: response.data.error || { code: 'EXPORT_ERROR', message: 'Request failed' },
        message: response.data.message,
      };
    }
    const pageData = response.data.data;
    return {
      success: true,
      data: {
        items: pageData.items.map(DashboardMapper.lectureToFrontend),
        hasMore: pageData.hasMore,
        nextCursor: pageData.nextCursor,
      },
      message: response.data.message,
    };
  },

  /**
   * Get optimized pending lectures for a specific device.
   * These come from a different bucket/collection and are not integrated into operations.
   */
  async getPendingLecturesByDeviceId(
    deviceId: string,
    params?: DashboardQueryParams
  ): Promise<ApiResponse<DashboardLecture[]>> {
    const pageResponse = await this.getPendingLecturesByDeviceIdPage(deviceId, params);
    if (!pageResponse.success) {
      return pageResponse;
    }
    return {
      success: true,
      data: pageResponse.data.items,
      message: pageResponse.message,
    };
  },

  async getPendingLecturesByDeviceIdPage(
    deviceId: string,
    params?: DashboardQueryParams
  ): Promise<ApiResponse<DashboardLecturePage>> {
    const response = await api.get<BackendApiResponse<BackendDashboardLecturePage>>(
      `/dashboard/pending-lectures/device/${deviceId}`,
      { params }
    );
    if (!response.data.success) {
      return {
        success: false,
        error: response.data.error || { code: 'EXPORT_ERROR', message: 'Request failed' },
        message: response.data.message,
      };
    }
    const pageData = response.data.data;
    return {
      success: true,
      data: {
        items: pageData.items.map(DashboardMapper.lectureToFrontend),
        hasMore: pageData.hasMore,
        nextCursor: pageData.nextCursor,
      },
      message: response.data.message,
    };
  },

  async exportLecturesByOperation(operationId: string, format: 'csv' | 'xlsx'): Promise<Blob> {
    const response = await api.get<Blob>(`/dashboard/lectures/${operationId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  async exportLecturesByDevice(deviceId: string, format: 'csv' | 'xlsx'): Promise<Blob> {
    const response = await api.get<Blob>(`/dashboard/lectures/device/${deviceId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  async exportPendingLecturesByDevice(deviceId: string, format: 'csv' | 'xlsx'): Promise<Blob> {
    const response = await api.get<Blob>(`/dashboard/pending-lectures/device/${deviceId}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },
};

// Export types for use in other components
// export type { DashboardQueryParams };
